import idx from 'idx';
import PropTypes from 'prop-types';
import * as React from 'react';
import { Query } from 'react-apollo';
import cx from 'classnames';

import ADMIN_CONSOLE_ACCESS_QUERY from '../../groups/queries/GroupViewHeaderAdminConsoleAccessQuery.gql';
import Button from 'components/Button';
import Buttons from 'components/Buttons';
import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
// @ts-ignore
import FreeClassLimitsLearnMoreDialog from 'groups/components/FreeClassLimitsLearnMoreDialog';
import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar';
import GroupNavigationTabs from 'groups/components/GroupNavigationTabs';
import Image from 'components/Image';
// @ts-ignore
import InviteButton from 'users/components/InviteButton';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import Text from 'components/Text';
import USER_QUERY from 'groups/queries/GroupViewHeaderUserQuery.gql';
import LimitedFreeClassAvatar from 'assets/images/avatars/limited-free-class-avatar.svg';

import {
  GroupViewHeaderAdminConsoleAccess,
  GroupViewHeaderAdminConsoleAccessVariables,
} from 'groups/queries/__generated__/GroupViewHeaderAdminConsoleAccess';
import { GroupViewHeaderUser } from 'groups/queries/__generated__/GroupViewHeaderUser';

import './styles/GroupViewHeader.scss';

export const limitedSendFunctionalityEventKeyPrefix =
  'free_product_limits.group_page.header.unavailable_banner';

// Props wihout any GraphQL data
type Props = {
  group: Group;
};

type GQLProps = Props & {
  groupViewHeaderUser: GroupViewHeaderUser;
  groupViewHeaderAdminConsoleAccess?:
    | (GroupViewHeaderAdminConsoleAccess & { loading: boolean })
    | { loading: boolean };
};

class GroupViewHeader extends React.Component<GQLProps> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  componentDidMount() {
    if (this.showLimitedSendFunctionality()) {
      this.trackEvent('view');
    }
  }

  trackEvent(event: string) {
    EventTracking.trackKey(`${limitedSendFunctionalityEventKeyPrefix}.${event}`);
  }

  showLimitedSendFunctionality = () => {
    const currentUser = CurrentUser.getUser();
    const { group } = this.props;
    const hasFreeProductLimitFeature =
      currentUser && currentUser.hasFeature('free_product_limits.web.apr-19');
    const hasLimitedSendFunctionality = group.get('hasLimitedSendFunctionality');
    return hasFreeProductLimitFeature && hasLimitedSendFunctionality;
  };

  renderLimitedSendFunctionalityButton = () => {
    const groupCode = this.props.group.getCodeOrError();
    const peopleListUrl = groupCode ? `/classes/${groupCode}/people` : '/classes';
    return (
      <ModalTrigger
        modal={
          <FreeClassLimitsLearnMoreDialog
            peopleListUrl={peopleListUrl}
            onRequestHide={() => {}}
            uiContext="group_header"
          />
        }>
        <Button
          onClick={e => {
            this.trackEvent('click');
            e.stopPropagation();
          }}
          shape="pill"
          level="secondary"
          color="lava"
          size="xxxsmall"
          data-test="limited-send-functionality-button">
          You cannot send announcements to this class
        </Button>
      </ModalTrigger>
    );
  };

  renderGroupAvatar = (group: Group, showLimitedSendFunctionality: boolean) => {
    if (showLimitedSendFunctionality) {
      return <Image width={40} src={LimitedFreeClassAvatar} />;
    }
    return <GroupAvatar group={group} size="medium" />;
  };

  renderGroupInfo = (group: Group, adminViewingPaidStaffClass?: boolean) => {
    const showLimitedSendFunctionality = this.showLimitedSendFunctionality();
    const showGroupCode = !group.isRoot() && !(group.getType() === 'district');
    return (
      <MediaObject center gutter="small">
        {this.renderGroupAvatar(group, showLimitedSendFunctionality)}
        <MediaObject gutter="small" inline figure="right">
          <div>
            <Text
              size="xlarge"
              element="div"
              color={adminViewingPaidStaffClass ? 'white' : 'pitch'}
              truncate>
              {group.getName()}
            </Text>
            {showGroupCode && (
              <Text element="div" color={adminViewingPaidStaffClass ? 'wolf' : 'thunder'}>
                @{group.getCodeOrError()}
              </Text>
            )}
          </div>
          {showLimitedSendFunctionality ? this.renderLimitedSendFunctionalityButton() : <span />}
        </MediaObject>
      </MediaObject>
    );
  };

  render() {
    const { group, groupViewHeaderUser, groupViewHeaderAdminConsoleAccess } = this.props;
    const { user } = groupViewHeaderUser;

    if (groupViewHeaderAdminConsoleAccess && groupViewHeaderAdminConsoleAccess.loading) {
      return <div className="GroupViewHeader-loading" />;
    }

    const adminConsoleAccess = idx(
      groupViewHeaderAdminConsoleAccess,
      // @ts-ignore // idx?
      _ => _.organizations[0].permissions.adminConsole.accessConsole
    );

    const peopleTab = this.context.router.location.pathname.match(/classes\/\w+\/people/);
    const canViewStaffClass = group.isStaffClass() && adminConsoleAccess;
    const adminViewingPaidStaffClass = canViewStaffClass ? canViewStaffClass : false;

    return (
      <div
        className={cx('GroupViewHeader', {
          'GroupViewHeader--dark': adminViewingPaidStaffClass,
        })}>
        <MediaObject figure="right" className="GroupViewHeader-top">
          <div className="GroupViewHeader-left">
            <div className="GroupViewHeader-left-group">
              {this.renderGroupInfo(group, adminViewingPaidStaffClass)}
            </div>
          </div>
          <div className="GroupViewHeader-right">
            <Buttons>
              {group.canAddPeople() && (
                <InviteButton
                  level={peopleTab ? 'primary' : 'secondary'}
                  forceType="button"
                  group={group}
                  color={adminViewingPaidStaffClass ? 'white' : 'brand'}
                />
              )}
            </Buttons>
          </div>
        </MediaObject>
        <div className="GroupViewHeader-tabs">
          <GroupNavigationTabs adminTheme={adminViewingPaidStaffClass} group={group} />
        </div>
      </div>
    );
  }
}

const GQLDataGroupViewHeader: React.StatelessComponent<Props> = ({ group }) => {
  return (
    <Query<GroupViewHeaderUser, {}> query={USER_QUERY}>
      {({ loading, error, data }) => {
        const userData = data;
        if (!loading && userData) {
          return (
            <Query<GroupViewHeaderAdminConsoleAccess, GroupViewHeaderAdminConsoleAccessVariables>
              query={ADMIN_CONSOLE_ACCESS_QUERY}
              variables={{ id: group.getStaffClassOrgId() }}
              skip={!group.isStaffClass()}>
              {({ loading, error, data }) => {
                // note that we dont care if we get undefined data, just let it go down as the skip might mean we get nothing back
                return (
                  <GroupViewHeader
                    group={group}
                    groupViewHeaderAdminConsoleAccess={{ ...data, loading }}
                    groupViewHeaderUser={userData}
                  />
                );
              }}
            </Query>
          );
        }
      }}
    </Query>
  );
};

export { GroupViewHeader };

export default GQLDataGroupViewHeader;
